import { ArrowRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { changedLines, type ContentChangeStage, type LineChange, type RequestEditView } from '@nest-recruit/shared';
import { EMPTY, formatDateTime } from '../../../lib/format';
import styles from './ChangeList.module.css';

/**
 * ADR 0088: a list of changes to what the requester wrote, newest first. Each shows the part, who and when, and only the lines
 * that changed, as before → after ("Years of experience: 5" → "Years of experience: 3"); the whole before and after open below
 * when there is more to them. Used by the request page's "Changes by HR" and the job's Changes.
 */

export interface ChangeListItem extends RequestEditView {
  /** Where it was changed; shown when the list holds both (the job's Changes). */
  readonly stage?: ContentChangeStage;
  /** A part this viewer may not see (the budget, to someone who may not see salaries): both sides read "—". */
  readonly isRestricted?: boolean;
}

const STAGE_LABELS: Readonly<Record<ContentChangeStage, string>> = {
  request: 'On the request',
  job: 'On the job',
};

export function ChangeList({ changes }: { readonly changes: readonly ChangeListItem[] }): ReactNode {
  return (
    <ul className={styles.list}>
      {changes.map((change) => (
        <li key={change.id} className={styles.entry}>
          <ChangeEntry change={change} />
        </li>
      ))}
    </ul>
  );
}

function lineCount(text: string): number {
  return text.split(/\r?\n/).filter((line) => line.trim() !== '').length;
}

/** True when the changed lines are the whole of both texts, so opening "the full before and after" would show nothing more. */
function isWholeText(change: ChangeListItem, lines: readonly LineChange[]): boolean {
  const before = lines.reduce((total, line) => total + line.before.length, 0);
  const after = lines.reduce((total, line) => total + line.after.length, 0);
  return before === lineCount(change.beforeText) && after === lineCount(change.afterText);
}

function ChangeEntry({ change }: { readonly change: ChangeListItem }): ReactNode {
  const lines = change.isRestricted ? [] : changedLines(change.beforeText, change.afterText);
  return (
    <>
      <div className={styles.head}>
        <b className={styles.part}>{change.partLabel}</b>
        {change.stage && <span className={styles.stage}>{STAGE_LABELS[change.stage]}</span>}
        <span className={styles.meta}>
          {change.editedByName ?? 'A former colleague'} · {formatDateTime(change.editedAt)}
        </span>
      </div>
      {change.isRestricted ? (
        <p className={styles.restricted}>
          <span className={styles.after}>{EMPTY}</span> Only people who may see salaries can see this change.
        </p>
      ) : lines.length === 0 ? (
        <p className={styles.restricted}>Only the spacing changed.</p>
      ) : (
        <ul className={styles.lines} aria-label="What changed">
          {lines.map((line, index) => (
            <li key={index} className={styles.line}>
              <Side lines={line.before} className={styles.before} label="Before" />
              <ArrowRight size={14} className={styles.arrow} aria-hidden />
              <Side lines={line.after} className={styles.after} label="After" />
            </li>
          ))}
        </ul>
      )}
      {!change.isRestricted && !isWholeText(change, lines) && (
        <details className={styles.full}>
          <summary className={styles.fullSummary}>Show the full before and after</summary>
          <div className={styles.fullBody}>
            <span className={styles.fullLabel}>Before</span>
            <p className={styles.fullText}>{change.beforeText}</p>
            <span className={styles.fullLabel}>After</span>
            <p className={styles.fullText}>{change.afterText}</p>
          </div>
        </details>
      )}
    </>
  );
}

function Side({ lines, className, label }: { readonly lines: readonly string[]; readonly className: string; readonly label: string }): ReactNode {
  return (
    <span className={`${styles.side} ${className}`}>
      <span className={styles.srOnly}>{label}: </span>
      {lines.length === 0 ? EMPTY : lines.join('\n')}
    </span>
  );
}
